import { Module } from 'vuex'
import {
  FileExtraProp,
  FileStatus,
  UploaderFile,
  UploaderInstance,
  UploaderState,
  UploadStatus,
} from '@pt/store/assets/uploader.type'
import { PTRootState } from '@pt/store/index.type'
import { checkAssets, createAssets, getAssetsUsage } from '@pt/api/assets'
import { getFileExtension, getFileName } from '@com/utils/file'
import { Message } from 'element-ui'
import router from '@pt/router'
import i18n from '@/lang'
import _ from 'lodash'

const generateExtraProp = (file: UploaderFile): FileExtraProp => {
  return {
    id: file.id,
    size: file.size,
    name: file.name,
    selected: false,
    status: FileStatus.waiting,
    errorMsg: '',
    file_url: '',
    progress: 0,
    md5: '',
  }
}

// 校验命名有前端和后端校验的逻辑，后端校验的优先级更高
// 1. 前端校验成功，发起后端校验，后端校验成功 —— 校验成功
// 2. 前端校验成功，发起后端校验，后端校验失败 —— 校验失败
// 3. 前端校验失败 —— 校验失败
const fileCheckFail = (
  file: FileExtraProp,
  online = false,
  errorMsg?: string,
): void => {
  file.status = online
    ? FileStatus.onlineCheckFail
    : FileStatus.offlineCheckFail
  file.errorMsg =
    errorMsg || String(i18n.t('playTurbo.assetLibrary.asset.upload.filetips'))
}

const fileCheckSuccess = (file: FileExtraProp, online = false): void => {
  file.status = online
    ? FileStatus.onlineCheckSuccess
    : FileStatus.offlineCheckSuccess
  file.errorMsg = ''
}

// @ts-ignore
const assetsUploaderModule: Module<UploaderState, PTRootState> = {
  namespaced: true,

  state: {
    uploaderInstance: null,
    uploadStatus: UploadStatus.waiting,
    dialogVisible: false,
    fileList: [],
    fileExtraList: [],
    freeSize: 0,
    totalSize: 0,
    usedSize: 0,
    totalAssets: 0,
  },

  mutations: {
    setUploaderInstance(state, instance: UploaderInstance) {
      state.uploaderInstance = instance
    },
    setDialogVisible(state, visible: boolean) {
      state.dialogVisible = visible
    },
    setUploadStatus(state, status: UploadStatus) {
      state.uploadStatus = status
    },
    syncFileList(state, fileList: UploaderFile[]) {
      state.fileList = fileList
      state.fileList.forEach((file) => {
        if (
          state.fileExtraList.findIndex(
            (item: FileExtraProp) => item.id === file.id,
          ) === -1
        ) {
          console.log('file', file)
          state.fileExtraList.push(generateExtraProp(file))
        }
      })
    },
    clearAllFile(state) {
      state.fileList = []
      state.fileExtraList = []
    },
    addFile(state, file: UploaderFile) {
      state.fileExtraList.push(generateExtraProp(file))
    },
    removeFile(state, file: UploaderFile) {
      state.fileList.splice(
        state.fileList.findIndex((item) => item.id === file.id),
        1,
      )
      state.fileExtraList.splice(
        state.fileExtraList.findIndex((item) => item.id === file.id),
        1,
      )
      // 如果已经删除干净了。隐藏弹框
      if (!state.fileList.length) {
        state.dialogVisible = false
        state.uploadStatus = UploadStatus.waiting
      }
    },
    selectFile(state, id: number) {
      const fileExtra = state.fileExtraList.find((file) => file.id === id)!
      if (fileExtra.status === FileStatus.onlineCheckSuccess) {
        fileExtra.selected = true
      }
    },
    selectAllFile(state) {
      state.fileExtraList.forEach((file) => {
        // 只有检测成功的资源,并且含有 md5 才允许勾选上传
        if (file.status === FileStatus.onlineCheckSuccess && file.md5) {
          file.selected = true
        }
      })
    },
    unSelectFile(state, id: number) {
      state.fileExtraList.find((file) => file.id === id)!.selected = false
    },
    clearSelectFile(state) {
      state.fileExtraList.forEach((file) => {
        file.selected = false
      })
    },
    checkIsSameName(state) {
      state.fileExtraList.forEach((file) => {
        // 只有 waiting 状态才进行前端校验
        if (file.status === FileStatus.waiting) {
          if (
            state.fileExtraList.filter((item) => item.name === file.name)
              .length > 1
          ) {
            fileCheckFail(file)
          } else {
            fileCheckSuccess(file)
          }
        }
      })
    },
    setUsage(
      state,
      {
        free_size,
        total_size,
        used_size,
        total_assets,
      }: {
        free_size: number
        total_size: number
        used_size: number
        total_assets: number
      },
    ) {
      state.freeSize = free_size
      state.totalSize = total_size
      state.usedSize = used_size
      state.totalAssets = total_assets
    },
    setCheckFailByOnline(
      state,
      obj: Partial<{ id: number; errorMsg?: string; status: FileStatus }>,
    ) {
      const file = state.fileExtraList.find((file) => file.id === obj.id)!
      if (file) {
        fileCheckFail(file, true, obj.errorMsg ? obj.errorMsg : '')
        // 如果有status值，直接用status赋值
        if (obj.status) {
          file.status = obj.status
        }
        // 如果检测不通过，需要去除勾选状态
        file.selected = false
      }
    },
    setCheckSuccessByOnline(state, id: number) {
      const file = state.fileExtraList.find((file) => file.id === id)!
      if (file) {
        fileCheckSuccess(file, true)
      }
    },
    resetFileStatus(state, id: number) {
      const file = state.fileExtraList.find((file) => file.id === id)!
      file.status = FileStatus.waiting
    },
    updateFileExtraProp(
      state,
      { id, data }: { id: number; data: Partial<FileExtraProp> },
    ) {
      const file = state.fileExtraList.find((file) => file.id === id)!
      Object.keys(data).forEach((key: string) => {
        // @ts-ignore
        file[key] = data[key]
      })
    },
  },

  actions: {
    async getAssetsUsage({ commit }) {
      try {
        const res = await getAssetsUsage()
        if (!res.data.code) {
          commit('setUsage', res.data.data)
        }
      } catch (e) {
        console.warn(e)
      }
    },
    async checkAssetsIsValid({ state, commit }, id: number) {
      try {
        const fileExtraProp = state.fileExtraList.find(
          (file: FileExtraProp) => file.id === id,
        )!
        // 只有通过前端校验并且校验通过才请求后端接口

        if (
          fileExtraProp &&
          fileExtraProp.status === FileStatus.offlineCheckSuccess
        ) {
          const res = await checkAssets({
            check_name: true,
            file_md5: fileExtraProp.md5,
            name: getFileName(fileExtraProp.name, true),
            format: getFileExtension(fileExtraProp.name),
            size: fileExtraProp.size,
          })
          if (!res.data.code) {
            const asset_id = _.get(res, 'data.data.id') // 资产库id,如果md5校验存在，则有该数据，否则没有
            const name_valid = _.get(res, 'data.data.name_valid') // 名称校验结果，true表示名称不存在，false表示名称已存在
            if (!asset_id) {
              commit('setCheckSuccessByOnline', id)
              if (!name_valid) {
                commit('setCheckFailByOnline', { id })
              }
            } else {
              commit('setCheckFailByOnline', {
                id,
                errorMsg: String(
                  i18n.t('playTurbo.assetLibrary.asset.upload.exists'),
                ),
              })
            }
          } else {
            commit('setCheckFailByOnline', { id })
          }
        }
      } catch (e) {
        commit('setCheckFailByOnline', { id })
        console.warn(e)
      }
    },

    removeAllFile({ state, commit }) {
      if (state.uploaderInstance) {
        state.uploaderInstance.cancel()
        commit('clearAllFile')
      }
    },

    async createAssets({ state, commit }, id: number) {
      const fileExtraProp = state.fileExtraList.find(
        (file: FileExtraProp) => file.id === id,
      )!
      try {
        // 只有上传成功的资源才可以上传资源库
        if (fileExtraProp.file_url) {
          const res = await createAssets({
            name: getFileName(fileExtraProp.name, true),
            file_url: fileExtraProp.file_url,
          })
          if (!res.data.code) {
            fileExtraProp.progress = 100
            fileExtraProp.status = FileStatus.uploadSuccess
          } else {
            fileExtraProp.status = FileStatus.uploadError
          }
        }
      } catch (e) {
        fileExtraProp.status = FileStatus.uploadError
        console.warn(e)
      }
    },

    handleCreateAssetsQueue({ state, commit, dispatch }) {
      setTimeout(() => {
        if (
          state.fileExtraList.some(
            (file: FileExtraProp) => file.status === FileStatus.uploading,
          )
        ) {
          dispatch('handleCreateAssetsQueue')
        } else {
          commit('setUploadStatus', UploadStatus.uploadEnd)
          commit('setDialogVisible', false)
          const msg = String(
            i18n.t('playTurbo.assetLibrary.asset.upload.total', {
              // selectNum: state.fileExtraList.length,
              failNum: state.fileExtraList.filter(
                (file: FileExtraProp) => file.status === FileStatus.uploadError,
              ).length,
              successNum: state.fileExtraList.filter(
                (file: FileExtraProp) =>
                  file.status === FileStatus.uploadSuccess,
              ).length,
            }),
          )
          Message.success(msg)
          setTimeout(() => {
            commit('setUploadStatus', UploadStatus.waiting)
            dispatch('removeAllFile')
            commit('setDialogVisible', false)
            // 刷新当前页面，通过第一页请求数据
            router.push({
              name: router.currentRoute.name,
              query: {
                ...router.currentRoute.query,
                page: '1',
                timeStamp: String(Number(new Date())),
              },
            })
          }, 1000)
        }
      }, 300)
    },
  },
}

export default assetsUploaderModule
